import { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "lucide-react";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { FiscalYear } from "@/types/fiscalYear";
import { useFormatters } from "@financialharmony/ui";
import { toast } from "sonner";
import { FiscalYearEditDialog } from "./FiscalYearEditDialog";

export const FiscalYearSettings = () => {
  const t = useTranslations('consultantSettings.fiscalYear');
  const { formatDate } = useFormatters();
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [accountingMethod, setAccountingMethod] = useState<'accrual' | 'cash'>('accrual');
  const [editingFiscalYear, setEditingFiscalYear] = useState<FiscalYear | null>(null);

  const fiscalYearService = ServiceRegistry.getFiscalYearService();

  useEffect(() => {
    loadFiscalYears();
  }, []);

  const loadFiscalYears = async () => {
    try {
      setLoading(true);
      const response = await fiscalYearService.getFiscalYears();
      setFiscalYears(response.items);
    } catch (error) {
      console.error('Error loading fiscal years:', error);
      toast.error(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    // Auto-fill end date to 1 year minus 1 day later
    if (value) {
      const start = new Date(value);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + 1);
      end.setDate(end.getDate() - 1);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };

  const handleCreateFiscalYear = async () => {
    if (!startDate || !endDate || !accountingMethod) {
      toast.error(t('fillAll'));
      return;
    }

    try {
      setLoading(true);
      await fiscalYearService.createFiscalYear({
        startDate,
        endDate,
        accountingMethod,
      });

      // Reset form
      setStartDate('');
      setEndDate('');
      setAccountingMethod('accrual');

      // Reload fiscal years
      await loadFiscalYears();
    } catch (error) {
      console.error('Error creating fiscal year:', error);
      // Error toast is already shown by the API client
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFiscalYear = async (fiscalYearId: string) => {
    try {
      setLoading(true);
      await fiscalYearService.closeFiscalYear(fiscalYearId);

      // Reload fiscal years
      await loadFiscalYears();
    } catch (error) {
      console.error('Error closing fiscal year:', error);
      // Error toast is already shown by the API client
    } finally {
      setLoading(false);
    }
  };

  const canCloseFiscalYear = (fiscalYear: FiscalYear): boolean => {
    if (fiscalYear.status === 'closed') return false;

    const endDate = new Date(fiscalYear.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate < today;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          {t('title')}
        </CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Create new fiscal year form */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
          <h3 className="font-semibold">{t('addTitle')}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">{t('startDate')}</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate">{t('endDate')}</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                {t('endHint')}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="accountingMethod">{t('method')}</Label>
            <Select
              value={accountingMethod}
              onValueChange={(value: 'accrual' | 'cash') => setAccountingMethod(value)}
              disabled={loading}
            >
              <SelectTrigger id="accountingMethod">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accrual">{t('accrual')}</SelectItem>
                <SelectItem value="cash">{t('cash')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleCreateFiscalYear}
            disabled={loading || !startDate || !endDate}
            className="w-full"
          >
            {loading ? t('creating') : t('create')}
          </Button>
        </div>

        {/* List of existing fiscal years */}
        <div className="space-y-2">
          <h3 className="font-semibold">{t('existing')}</h3>

          {loading && fiscalYears.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : fiscalYears.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('empty')}
            </p>
          ) : (
            <div className="space-y-2">
              {fiscalYears.map((fy) => (
                <div
                  key={fy.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {formatDate(fy.startDate, 'numeric')} - {formatDate(fy.endDate, 'numeric')}
                      </span>
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          fy.status === 'open'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {t(`status.${fy.status}`)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {fy.accountingMethod === 'accrual' ? t('accrual') : t('cash')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {fy.status === 'open' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingFiscalYear(fy)}
                        disabled={loading}
                      >
                        {t('edit')}
                      </Button>
                    )}
                    {canCloseFiscalYear(fy) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCloseFiscalYear(fy.id)}
                        disabled={loading}
                      >
                        {t('close')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {editingFiscalYear && (
        <FiscalYearEditDialog
          fiscalYear={editingFiscalYear}
          open={!!editingFiscalYear}
          onOpenChange={(next) => {
            if (!next) setEditingFiscalYear(null);
          }}
          onSaved={loadFiscalYears}
        />
      )}
    </Card>
  );
};
